import { create } from 'zustand';

export interface Module {
  id: string;
  name: string;
  description?: string;
  api_endpoint: string;
  auth_type: string;
  oauth_config?: any;
  json_schema?: any;
  field_mappings?: any;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface UploadedFile {
  id: string;
  original_filename: string;
  stored_filename: string;
  file_path: string;
  mime_type: string;
  file_size: number;
  is_processed: boolean;
  metadata?: any;
  created_at: string;
}

interface AppState {
  // File upload state
  currentFile: UploadedFile | null;
  extractedData: any;
  editedData: any;
  selectedModule: Module | null;
  isProcessing: boolean;
  
  // Modules
  availableModules: Module[];
  oauthStatus: Record<string, boolean>;
  
  // UI state
  showLoginModal: boolean;
  showRegisterModal: boolean;
  sidebarOpen: boolean;
  
  // Actions
  setCurrentFile: (file: UploadedFile | null) => void;
  setExtractedData: (data: any) => void;
  setEditedData: (data: any) => void;
  setSelectedModule: (module: Module | null) => void;
  setProcessing: (processing: boolean) => void;
  setAvailableModules: (modules: Module[]) => void;
  setOAuthStatus: (moduleId: string, status: boolean) => void;
  setShowLoginModal: (show: boolean) => void;
  setShowRegisterModal: (show: boolean) => void;
  setSidebarOpen: (open: boolean) => void;
  clearFileData: () => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  // Initial state
  currentFile: null,
  extractedData: null,
  editedData: null,
  selectedModule: null,
  isProcessing: false,
  availableModules: [],
  oauthStatus: {},
  showLoginModal: false,
  showRegisterModal: false,
  sidebarOpen: true,

  // Actions
  setCurrentFile: (file) => set({ currentFile: file }),
  
  setExtractedData: (data) => set({ 
    extractedData: data,
    editedData: data // Initialize edited data with extracted data
  }),
  
  setEditedData: (data) => set({ editedData: data }),
  
  setSelectedModule: (module) => set({ selectedModule: module }),
  
  setProcessing: (processing) => set({ isProcessing: processing }),
  
  setAvailableModules: (modules) => set({ availableModules: modules }),
  
  setOAuthStatus: (moduleId, status) => set((state) => ({
    oauthStatus: {
      ...state.oauthStatus,
      [moduleId]: status
    }
  })),
  
  setShowLoginModal: (show) => set({ showLoginModal: show }),
  
  setShowRegisterModal: (show) => set({ showRegisterModal: show }),
  
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  
  clearFileData: () => set({
    currentFile: null,
    extractedData: null,
    editedData: null,
    selectedModule: null,
    isProcessing: false
  })
}));